import type { QueryClient } from '@tanstack/react-query';

import { writeActiveWorkspacePointer } from './activeWorkspacePointer';
import { isWorkspaceIndependentKey } from './workspaceCacheScope';
import { runWorkspaceResets } from './workspaceResetRegistry';

import { idbPersister } from '@/config/queryClient';
import { broadcastWorkspaceChanged } from '@/hooks/useWorkspaceBroadcastSync';

export interface SwitchWorkspaceArgs {
  userId: string;
  brandKitId: string;
  queryClient: QueryClient;
}

/**
 * Switch the acting workspace, and leave nothing of the previous one behind.
 *
 * ORDER MATTERS AND IS LOAD-BEARING. Each step exists because of a specific way
 * the previous workspace's data survives otherwise:
 *
 * 1. **Cancel in-flight mutations first.** An optimistic `onError` handler runs
 *    `setQueryData(key, context.previous)`, which RE-CREATES an entry on an
 *    already-cleared cache from a pre-switch snapshot. Clearing before the
 *    mutations settle does not defend against this; cancelling does.
 * 2. **Write the pointer, and stop if it fails.** The DB trigger refuses a kit
 *    that is archived or belongs to another account. If the write is rejected
 *    the switch did not happen, so nothing else may run --- otherwise the UI
 *    clears itself for a move it never made.
 * 3. **Clear the query cache**, keeping the workspace-independent keys above.
 * 4. **Clear the IndexedDB persister.** `queryClient.clear()` does NOT call
 *    `persister.removeClient()`, and persistence is throttled (~1s). Without
 *    this, switching and closing the tab inside that window restores the
 *    PREVIOUS workspace's entire cache on next load and treats it as fresh for
 *    24 hours. This is the least obvious step and the most damaging to omit.
 * 5. **Reset in-memory stores**, which the query cache never held.
 *
 * Realtime channels are intentionally NOT torn down here: they are owned by the
 * hooks that create them, and those hooks re-subscribe when their queries
 * refetch in step 3. Tearing them down from here would race that.
 */
export async function switchWorkspace({
  userId,
  brandKitId,
  queryClient,
}: SwitchWorkspaceArgs): Promise<void> {
  await queryClient.getMutationCache().clear();

  await writeActiveWorkspacePointer(userId, brandKitId);

  // Publish the new pointer directly rather than refetching it.
  //
  // ⚠️ This line is load-bearing and its absence was a real bug, caught by
  // `useBrandKits.test.tsx`: the pointer key is deliberately in the
  // workspace-INDEPENDENT list (so a switch does not wipe the switcher's own
  // state), which also means the sweep below will neither remove nor
  // invalidate it — so without this write the UI kept rendering the PREVIOUS
  // workspace even though the server had already accepted the new one. We know
  // the value we just persisted, so a round trip would be both slower and less
  // certain.
  queryClient.setQueryData(['active-workspace-pointer', userId], brandKitId);
  // The resolved workspace follows the pointer: a pinned kit always wins
  // resolve_effective_brand_kit_id(), and the ownership trigger refuses a
  // pointer at a kit this user cannot access. Publish it the same way so a
  // mounted gate (e.g. Reach) never keeps the previous workspace.
  queryClient.setQueryData(['effective-workspace-id', userId], brandKitId);

  queryClient.removeQueries({
    predicate: (query) => !isWorkspaceIndependentKey(query.queryKey),
  });

  try {
    await idbPersister.removeClient();
  } catch (err) {
    // A failed persister wipe is not fatal to the switch, but it IS the step
    // that would silently resurrect the old workspace, so it must be loud.
    console.error('[workspace] could not clear the persisted query cache', err);
  }

  runWorkspaceResets();

  await queryClient.invalidateQueries({
    predicate: (query) => !isWorkspaceIndependentKey(query.queryKey),
  });

  // Every other open tab is now showing the previous workspace. The pointer is
  // ONE row per user, so those tabs are not merely stale — they are wrong, and
  // they have no way to notice on their own. Told last, so a switch that threw
  // above never announces itself.
  broadcastWorkspaceChanged(brandKitId);
}
